import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import { VisualSlide } from "./visualText";

const LOCAL_DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm";

export type AgentTextPart = { type: "text"; text: string };
export type AgentToolCallPart = {
  type: "tool_call";
  id: string;
  name: string;
  input: Record<string, any>;
};
export type AgentToolResultPart = {
  type: "tool_result";
  toolCallId: string;
  output: string;
  isError?: boolean;
  images?: AgentImageRefPart[]; // pictures the tool wants the model to see, loaded by the scheduler
  tagged?: TaggedProfile[]; // for the chat only: the scheduler hands the model nothing but output and isError
};

// Someone a caption change tagged, as the mention search described them, so the user can check it is the right person.
export interface TaggedProfile {
  service: string;
  name: string;
  image: string | null;
  description: string | null; // the LinkedIn headline or the Instagram bio
  kind: string | null; // the channel service type: profile for a person, business for a page
}
export interface AgentImageRefPart {
  type: "image_ref";
  url: string;
  mimeType: string;
  name?: string;
  width?: number;
  height?: number;
}

export interface AgentFilePart {
  type: "file";
  name: string;
  mimeType: string;
  text: string;
}

export interface AgentEventPart {
  type: "event"; // raised by the composer itself, not typed by the user
  text: string;
  quiet?: boolean; // a check the user did not ask for; shown only once the reply has something to say
}

export interface AgentOpeningPart {
  type: "opening"; // the chat was just opened; the model answers with the greeting and the options that fit the post
}

export interface AgentThinkingPart {
  type: "thinking"; // the model's private reasoning, passed back untouched with the tool calls it led to
  thinking: string;
  signature: string;
}

export interface AgentRedactedThinkingPart {
  type: "redacted_thinking";
  data: string;
}

export type AgentPart =
  | AgentTextPart
  | AgentToolCallPart
  | AgentToolResultPart
  | AgentImageRefPart
  | AgentFilePart
  | AgentEventPart
  | AgentOpeningPart
  | AgentThinkingPart
  | AgentRedactedThinkingPart;

export interface AgentMessage {
  role: "user" | "assistant" | "tool";
  content: AgentPart[];
}

export interface AgentStepResponse {
  stopReason: "tool_call" | "end_turn" | "max_tokens";
  content: AgentPart[];
}

export type AgentStepEvent =
  | { type: "text"; text: string } // a fragment of the reply as it is written
  | { type: "result"; result: AgentStepResponse } // the complete step, always last on success
  | { type: "error"; message: string };

export interface SnapshotChannel {
  id: string;
  service: string;
  serviceType: string;
  name: string;
  selected: boolean;
  connected: boolean;
}

export interface SnapshotImage {
  index: number;
  width: number | null;
  height: number | null;
  aspectRatio: string | null; // width divided by height, two decimals
}

export interface SnapshotVisual {
  slides: VisualSlide[]; // every text element per slide, addressed as [slide.index]
  clippedSlides: number[] | null; // slides whose text overflows the canvas, null until the editor measured
  previewAvailable: boolean;
}

export interface SnapshotIssue {
  service: string | null;
  type: string;
  message: string;
}

export interface ComposerSnapshot {
  now: string;
  timezone: string;
  inDraftMode: boolean;
  isExistingPost: boolean;
  channelsLocked: boolean;
  channels: SnapshotChannel[];
  inSplitMode: boolean;
  visibleCaption: string;
  captions: Record<string, string>;
  captionAuthors: Record<string, "assistant" | "user">; // who wrote each non-empty caption
  tags: Record<string, SnapshotTag[]>; // tags per service, as their text appears in that caption
  attachments: { type: string | null; count: number; images: SnapshotImage[]; issues: SnapshotIssue[]; visual: SnapshotVisual | null };
  schedule: { optionId: string | null; pickedDate: string | null };
  category: {
    contentTypeId: string | null;
    options: { id: string; title: string }[];
  };
  validationErrors: Record<string, unknown>;
  undoableChanges?: string[]; // changes the assistant made in this conversation that undo_change can revert, oldest first
}

export interface SnapshotSource {
  now: Date;
  timezone: string;
  inDraftMode: boolean;
  postId?: string;
  isDraft?: boolean;
  isPostGroup?: boolean;
  selectedChannelIds: string[];
  enabledServices: string[];
  accountChannels: {
    id: string;
    service: string;
    serviceType: string;
    username: string;
    connectionStatus?: string;
  }[];
  inSplitMode: boolean;
  visibleCaption: string;
  captions: Record<string, string>;
  assistantCaptionTexts: string[]; // every caption text the assistant wrote in this conversation
  mentions: (MentionRef & { addedBy: "assistant" | "user" })[];
  attachmentType: string | null;
  attachmentCount: number;
  attachmentImages: { width?: number; height?: number }[];
  attachmentIssues: { service?: string; type: string; message: unknown }[]; // message may be a React node
  visual?: SnapshotVisual | null; // the Willow Create visual, when the post carries one
  schedulerOptionId: string | null;
  pickedDate: Date | null;
  contentTypeId: string | null;
  categoryOptions: { id: string; title: string }[];
  validationErrors: Record<string, unknown>;
  undoableChanges?: string[];
}

export interface MentionRef {
  service: string;
  id: string; // handle on X and Instagram, URN or page id elsewhere
  text: string; // display name
}

export interface SnapshotTag {
  text: string;
  addedBy: "assistant" | "user";
}

export const CAPTION_SERVICES = ["linkedin", "instagram", "facebook", "twitter"];
const HANDLE_SERVICES = ["twitter", "instagram"];

const SERVICE_LABELS: Record<string, string> = {
  linkedin: "LinkedIn",
  instagram: "Instagram",
  facebook: "Facebook",
  twitter: "X"
};

const RATIO_LABELS: Record<string, string> = {
  square: "1:1",
  portrait: "4:5",
  landscape: "16:9"
};

export const serviceLabel = (service: string): string => SERVICE_LABELS[service] ?? service;

// A Facebook page and an Instagram account often share a name, so the network tells them apart.
export const channelLabel = (channel: Pick<SnapshotChannel, "name" | "service">): string => `${channel.name} (${serviceLabel(channel.service)})`;

export const selectedChannelIds = (channels: Pick<SnapshotChannel, "id" | "selected">[]): string[] =>
  channels.filter(channel => channel.selected).map(channel => channel.id);

// LinkedIn only finds people by the slug after linkedin.com/in/, so a full name is retried as its likely slugs.
export const linkedinSlugVariants = (query: string): string[] => {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length < 2) return [];
  return [words.join(""), words.join("-")];
};

// The exact text the editor matches when it turns a mention back into a tag: the handle on X and Instagram, the name elsewhere.
export const mentionTextFor = ({ service, id, text }: MentionRef): string => `@${HANDLE_SERVICES.includes(service) ? id : text}`;

const squashName = (text: string): string =>
  text.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().replace(/[\s._-]+/g, "");

// Squashed, so a slug such as "ludwigdumont" still finds "Ludwig Dumont", and "Dante" finds "Danté".
export const matchesMentionQuery = (candidate: Pick<MentionRef, "text">, query: string): boolean =>
  squashName(candidate.text).includes(squashName(query));

// The scheduler lists every connected channel ahead of the search hits, whatever the query, so the list is ranked before it is cut.
export const rankMentionCandidates = <T extends Pick<MentionRef, "text"> & { isChannelConnection?: unknown }>(candidates: T[], query: string): T[] => {
  const matching = candidates.filter(candidate => matchesMentionQuery(candidate, query));
  const rest = candidates.filter(candidate => !matching.includes(candidate));
  return [...matching, ...rest.filter(candidate => !candidate.isChannelConnection), ...rest.filter(candidate => candidate.isChannelConnection)];
};

export const formatAspectRatio = (width?: number | null, height?: number | null): string | null =>
  width && height ? (width / height).toFixed(2) : null;

export const reactNodeToText = (node: unknown): string => {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(reactNodeToText).join("");
  if (typeof node === "object" && "props" in node) {
    return reactNodeToText((node as { props: { children?: unknown } }).props.children);
  }
  return "";
};

// "sv-SE" is the one locale whose default format is already YYYY-MM-DD HH:mm:ss
export const formatLocalDateTime = (date: Date, timezone?: string): string => {
  if (!timezone) return format(date, LOCAL_DATE_TIME_FORMAT);
  try {
    return formatInTimeZone(date, timezone, LOCAL_DATE_TIME_FORMAT);
  } catch {
    return date.toISOString().slice(0, 16);
  }
};

export const truthyEntries = (record: Record<string, unknown> | null | undefined): Record<string, unknown> =>
  Object.keys(record ?? {}).reduce<Record<string, unknown>>((acc, key) => {
    const value = record![key];
    if (value) acc[key] = value;
    return acc;
  }, {});

export const buildSnapshot = (source: SnapshotSource): ComposerSnapshot => {
  const channels = source.accountChannels.map(channel => ({
    id: channel.id,
    service: channel.service,
    serviceType: channel.serviceType,
    name: channel.username,
    selected: source.selectedChannelIds.includes(channel.id),
    connected: channel.connectionStatus === "connected"
  }));

  const captions = source.inSplitMode
    ? source.enabledServices.reduce<Record<string, string>>((acc, service) => {
        acc[service] = source.captions[service] ?? "";
        return acc;
      }, {})
    : { all: source.captions.all ?? "" };

  return {
    now: formatLocalDateTime(source.now, source.timezone),
    timezone: source.timezone,
    inDraftMode: source.inDraftMode,
    isExistingPost: !!source.postId,
    channelsLocked: !!source.postId && !source.isDraft && !source.isPostGroup,
    channels,
    inSplitMode: source.inSplitMode,
    visibleCaption: source.visibleCaption,
    captions,
    captionAuthors: Object.keys(captions).reduce<Record<string, "assistant" | "user">>((authors, key) => {
      if (captions[key].trim()) authors[key] = source.assistantCaptionTexts.includes(captions[key]) ? "assistant" : "user";
      return authors;
    }, {}),
    tags: source.mentions.reduce<Record<string, SnapshotTag[]>>((tags, mention) => {
      tags[mention.service] = [...(tags[mention.service] ?? []), { text: mentionTextFor(mention), addedBy: mention.addedBy }];
      return tags;
    }, {}),
    attachments: {
      type: source.attachmentType,
      count: source.attachmentCount,
      images: source.attachmentImages.map((image, index) => ({
        index,
        width: image.width ?? null,
        height: image.height ?? null,
        aspectRatio: formatAspectRatio(image.width, image.height)
      })),
      issues: source.attachmentIssues
        .filter(issue => issue.type !== "info")
        .map(issue => ({
          service: issue.service ?? null,
          type: issue.type,
          message: reactNodeToText(issue.message).replace(/\s+/g, " ").trim()
        })),
      visual: source.visual ?? null
    },
    schedule: {
      optionId: source.schedulerOptionId,
      pickedDate: source.pickedDate ? formatLocalDateTime(source.pickedDate, source.timezone) : null
    },
    category: {
      contentTypeId: source.contentTypeId,
      options: source.categoryOptions
    },
    undoableChanges: source.undoableChanges ?? [],
    validationErrors: truthyEntries(source.validationErrors)
  };
};

export const userMessage = (text: string, attachments: AgentPart[] = []): AgentMessage => ({
  role: "user",
  content: [{ type: "text", text }, ...attachments]
});

export const eventMessage = (text: string, { quiet = false }: { quiet?: boolean } = {}): AgentMessage => ({
  role: "user",
  content: [{ type: "event", text, ...(quiet ? { quiet } : {}) }]
});

export const openingMessage = (): AgentMessage => ({ role: "user", content: [{ type: "opening" }] });

export const isOpeningMessage = (content: AgentPart[]): boolean => content.some(part => part.type === "opening");

export const getEventText = (content: AgentPart[]): string | null => {
  const part = content.find((candidate): candidate is AgentEventPart => candidate.type === "event");
  return part ? part.text : null;
};

export const isQuietEvent = (content: AgentPart[]): boolean => content.some(part => part.type === "event" && !!part.quiet);

// Must match the reply the system prompt asks for when a quiet check finds nothing to say.
export const SILENT_REPLY = "[No reply]";

export const isSilentReply = (message: AgentMessage): boolean =>
  message.role === "assistant" && getToolCalls(message.content).length === 0 && getText(message.content).trim() === SILENT_REPLY;

// True while streamed text could still turn out to be the silent reply, so the chat holds it back.
export const couldBeSilentReply = (text: string): boolean => {
  const trimmed = text.trim();
  return !trimmed || SILENT_REPLY.startsWith(trimmed);
};

export interface AssistantRows {
  continued: Set<number>; // assistant messages that carry on the row above, so they show no avatar of their own
  trailingHead: number | "lead" | null; // the first row of the assistant block the thread ends with; "lead" is the row above the history
}

// Consecutive assistant messages read as one turn under one avatar; tool results and the opening render nothing in between.
export const layoutAssistantRows = (history: AgentMessage[], hidden: Set<number>, hasLeadRow: boolean): AssistantRows => {
  const continued = new Set<number>();
  let head: number | "lead" | null = hasLeadRow ? "lead" : null;
  history.forEach((message, index) => {
    if (hidden.has(index) || message.role === "tool" || isOpeningMessage(message.content)) return;
    if (message.role !== "assistant") {
      head = null;
      return;
    }
    if (head === null) head = index;
    else continued.add(index);
  });
  return { continued, trailingHead: head };
};

// A silent reply and the quiet event it answers are kept for the model but never shown or logged.
export const hiddenTurnIndexes = (history: AgentMessage[]): Set<number> => {
  const hidden = new Set<number>();
  history.forEach((message, index) => {
    const previous = history[index - 1];
    if (!isSilentReply(message)) return;
    hidden.add(index);
    if (previous && isQuietEvent(previous.content)) hidden.add(index - 1);
  });
  const last = history[history.length - 1];
  if (last && isQuietEvent(last.content)) hidden.add(history.length - 1);
  return hidden;
};

export interface AttachmentSummary {
  name: string;
  kind: "image" | "file";
}

export const getAttachments = (content: AgentPart[]): AttachmentSummary[] =>
  content.flatMap((part): AttachmentSummary[] => {
    if (part.type === "image_ref") return [{ name: part.name ?? part.url, kind: "image" }];
    if (part.type === "file") return [{ name: part.name, kind: "file" }];
    return [];
  });

export const toolResultMessage = (results: AgentToolResultPart[]): AgentMessage => ({ role: "tool", content: results });

export const getText = (content: AgentPart[]): string =>
  content
    .filter((part): part is AgentTextPart => part.type === "text")
    .map(part => part.text)
    .join("\n")
    .trim();

export const getToolCalls = (content: AgentPart[]): AgentToolCallPart[] =>
  content.filter((part): part is AgentToolCallPart => part.type === "tool_call");

const isThinking = (part: AgentPart): boolean => part.type === "thinking" || part.type === "redacted_thinking";

// The model only needs its reasoning back for the latest assistant turn; older blocks just bloat the request.
export const stripStaleThinking = (messages: AgentMessage[]): AgentMessage[] => {
  const lastAssistant = messages.map(message => message.role).lastIndexOf("assistant");
  return messages.map((message, index) =>
    message.role === "assistant" && index !== lastAssistant ? { ...message, content: message.content.filter(part => !isThinking(part)) } : message
  );
};

export const indexToolResults = (history: AgentMessage[]): Record<string, AgentToolResultPart> =>
  history.reduce<Record<string, AgentToolResultPart>>((acc, message) => {
    message.content.forEach(part => {
      if (part.type === "tool_result") acc[part.toolCallId] = part;
    });
    return acc;
  }, {});

const retryKey = (call: AgentToolCallPart): string => `${call.name}:${call.input?.target ?? ""}`;

// Failed calls the model's next message retried with the same tool and target, successfully; showing them would report an error the user never hit.
export const findRecoveredFailures = (history: AgentMessage[], results: Record<string, AgentToolResultPart>): Set<string> => {
  const recovered = new Set<string>();
  const assistantIndexes = history.flatMap((message, index) => (message.role === "assistant" ? [index] : []));
  assistantIndexes.forEach((index, position) => {
    const next = history[assistantIndexes[position + 1]];
    if (!next) return;
    const retried = new Set(getToolCalls(next.content).filter(call => results[call.id] && !results[call.id].isError).map(retryKey));
    getToolCalls(history[index].content)
      .filter(call => results[call.id]?.isError && retried.has(retryKey(call)))
      .forEach(call => recovered.add(call.id));
  });
  return recovered;
};

export const emptiedTurnIndexes = (history: AgentMessage[], hiddenCallIds: Set<string>): number[] =>
  history.flatMap((message, index) => {
    const calls = getToolCalls(message.content);
    const emptied = message.role === "assistant" && !getText(message.content) && calls.length > 0 && calls.every(call => hiddenCallIds.has(call.id));
    return emptied ? [index] : [];
  });

interface ToolCallForms {
  done: string; // past tense, once the tool has run
  doing: string; // present tense, while it runs
}

const forms = (done: string, doing: string): ToolCallForms => ({ done, doing: `${doing}…` });

export type ProfileName = { id: string; name: string | null };
// Who chose the chat's profile; must match PROFILE_CHOOSERS in the scheduler's composerAgent.
export type ProfileChooser = "user" | "create" | "assistant";

const describeToolCallForms = (call: AgentToolCallPart, channels: Pick<SnapshotChannel, "id" | "name">[], profiles: ProfileName[]): ToolCallForms => {
  const input = call.input ?? {};
  switch (call.name) {
    case "set_profile": {
      const profile = profiles.find(candidate => candidate.id === input.profileId);
      return profile ? forms(`Writing as ${profile.name ?? "Untitled profile"}`, `Switching to ${profile.name ?? "Untitled profile"}`) : forms("Switched the profile", "Switching the profile");
    }
    case "write_caption":
      return input.previousAttempt ? forms("Rewrote the caption with Willow Create", "Rewriting the caption with Willow Create") : forms("Wrote the caption with Willow Create", "Writing the caption with Willow Create");
    case "set_caption": {
      const tagCount = Array.isArray(input.mentions) ? input.mentions.length : 0;
      const tagged = tagCount ? ` with ${tagCount} tag${tagCount === 1 ? "" : "s"}` : "";
      const which = input.target === "all" ? "the caption" : `the ${serviceLabel(input.target)} caption`;
      return forms(`Updated ${which}${tagged}`, `Updating ${which}${tagged}`);
    }
    case "search_mentions":
      return forms(`Searched ${serviceLabel(input.service)} for "${input.query}"`, `Searching ${serviceLabel(input.service)} for "${input.query}"`);
    case "set_split_mode":
      return input.enabled ? forms("Split the captions per channel", "Splitting the captions per channel") : forms("Merged the captions", "Merging the captions");
    case "set_channels": {
      const names = (input.channelIds ?? []).map((id: string) => channels.find(channel => channel.id === id)?.name ?? id);
      return names.length ? forms(`Selected ${names.join(", ")}`, `Selecting ${names.join(", ")}`) : forms("Cleared the channels", "Clearing the channels");
    }
    case "set_schedule":
      switch (input.mode) {
        case "custom": {
          const when = String(input.dateTime).replace("T", " at ");
          return forms(`Scheduled for ${when}`, `Scheduling for ${when}`);
        }
        case "best_time":
          return forms(`Picked the best time on ${input.date}`, `Picking the best time on ${input.date}`);
        case "auto_schedule":
          return forms("Set to auto-schedule", "Setting to auto-schedule");
        default:
          return forms("Set to post now", "Setting to post now");
      }
    case "set_category":
      return forms(`Category set to ${input.contentTypeId}`, `Setting the category to ${input.contentTypeId}`);
    case "search_images":
      return forms(`Searched images for "${input.query}"`, `Searching images for "${input.query}"`);
    case "attach_image":
      return forms("Attached an image", "Attaching an image");
    case "generate_image":
      return forms("Generated an image", "Generating an image");
    case "fit_image": {
      const target = `image ${Number(input.index) + 1} to ${RATIO_LABELS[input.aspectRatio] ?? input.aspectRatio}`;
      return input.method === "extend" ? forms(`Extended ${target}`, `Extending ${target}`) : forms(`Cropped ${target}`, `Cropping ${target}`);
    }
    case "restore_image":
      return forms(`Restored image ${Number(input.index) + 1}`, `Restoring image ${Number(input.index) + 1}`);
    case "get_recent_posts":
      return input.query
        ? forms(`Looked up posts about "${input.query}"`, `Looking up posts about "${input.query}"`)
        : forms("Looked up recent posts", "Looking up recent posts");
    case "read_url":
      return forms(`Read ${input.url}`, `Reading ${input.url}`);
    case "web_search":
      return forms(`Searched the web for "${input.query}"`, `Searching the web for "${input.query}"`);
    case "attach_uploaded_image":
      return forms("Attached your image", "Attaching your image");
    case "remove_attachments":
      return forms("Removed the attachments", "Removing the attachments");
    case "create_visual": {
      const kind = input.format === "carousel" ? "carousel" : "visual";
      return forms(`Created a Willow Create ${kind}`, `Creating a Willow Create ${kind}, this takes 1–2 minutes`);
    }
    case "view_visual":
      return forms("Looked at the visual", "Looking at the visual");
    case "set_visual_text":
      return forms(`Changed a text on slide ${Number(input.slide) + 1} of the visual`, `Changing a text on slide ${Number(input.slide) + 1} of the visual`);
    case "edit_visual":
      return forms("Edited the visual", "Editing the visual");
    case "undo_change":
      return forms("Undid the last change", "Undoing the last change");
    default:
      return { done: call.name, doing: call.name };
  }
};

export const describeToolCall = (call: AgentToolCallPart, channels: Pick<SnapshotChannel, "id" | "name">[], profiles: ProfileName[] = []): string =>
  describeToolCallForms(call, channels, profiles).done;

export const describeToolCallInProgress = (call: AgentToolCallPart, channels: Pick<SnapshotChannel, "id" | "name">[], profiles: ProfileName[] = []): string =>
  describeToolCallForms(call, channels, profiles).doing;

const OPTIONS_LINE = /^\s*options:\s*(.+?)\s*$/i;
const MAX_REPLY_OPTIONS = 4;

// The model ends a question with "Options: a | b"; that line becomes buttons instead of text.
// The reply keeps its markdown for rendering; the options line and the options themselves are read as plain text.
export const splitReplyOptions = (text: string): { text: string; options: string[] } => {
  const lines = text.trimEnd().split("\n");
  const match = stripMarkdown(lines[lines.length - 1]).match(OPTIONS_LINE);
  if (!match) return { text, options: [] };
  const options = match[1]
    .split("|")
    .map(option => option.trim())
    .filter(Boolean)
    .slice(0, MAX_REPLY_OPTIONS);
  return { text: lines.slice(0, -1).join("\n").trimEnd(), options };
};

// Markdown folds a single newline into a space; the model uses one for a line break, as the plain-text days taught it.
export const markdownWithLineBreaks = (text: string): string => text.replace(/([^\n])\n(?!\n)/g, "$1  \n");

export type StreamedEvent<TResult> = { type: "result"; result: TResult } | { type: "error"; message: string };

export const parseStreamedEvent = <TResult>(raw: string): StreamedEvent<TResult> => {
  const event = JSON.parse(raw) as { type?: unknown; result?: TResult; message?: unknown } | null;
  if (event?.type === "result" && event.result !== undefined) return { type: "result", result: event.result };
  if (event?.type === "error" && typeof event.message === "string") return { type: "error", message: event.message };
  throw new Error("Unexpected event");
};

export const parseStepEvent = (raw: string): AgentStepEvent => {
  const event = JSON.parse(raw) as Partial<AgentStepEvent> | null;
  if (event?.type === "text" && typeof event.text === "string") return { type: "text", text: event.text };
  if (event?.type === "result" && event.result && Array.isArray(event.result.content)) return { type: "result", result: event.result };
  if (event?.type === "error" && typeof event.message === "string") return { type: "error", message: event.message };
  throw new Error("Unexpected step event");
};

// A half-written "Options:" line, down to its first letters, is hidden while streaming instead of flashing before it becomes buttons.
const STREAMING_OPTIONS_LINE = /(^|\n)[ \t]*(?:options:?[^\n]*|o|op|opt|opti|optio|option)$/i;

export const streamedReplyText = (text: string): string => stripMarkdown(text).replace(STREAMING_OPTIONS_LINE, "").trimEnd();

export const stripMarkdown = (text: string): string =>
  text
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^(\s*)[-*]\s+/gm, "$1• ")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/(^|[\s(])\*([^*\n]+?)\*(?=[\s).,!?:;]|$)/gm, "$1$2")
    .replace(/`([^`\n]+)`/g, "$1");
